import { getContextFromFeaturePrd } from "@/components/AI/EngSpec/utils";
import { AI_AGENT, useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { Message, MessageContext } from "@/store/zustand/supabaseStore";
import { useSession } from "@clerk/clerk-react";
import { v4 as uuidv4 } from "uuid";
import { ChatMessageOptions } from "../types";
import {
  fixJson,
  getAxiosErorrMessage,
  logAxiosError,
  useCanvasId,
} from "../utils";
import { useBaseInputsForPrompt } from "./prompt";
import { generateJsonStream } from "./utils";
import {
  useCreateMessageForChat,
  parseMessageContentBlocks,
} from "@/components/AI/chat/utils/messages";
import { track } from "../events/tracker";
import { countEligibleTokensFromSegments } from "../tokenCounter";
import useActivityStore from "@/store/zustand/activityStore";
import { toast } from "sonner";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";
import { useEffect } from "react";
import { FeaturePRD } from "@/types/issues";
import { useState } from "react";

const MAX_PREVIOUS_MESSAGES = 10;

/**
 * Tool calls from wrapper agents that should be filtered out from the UI.
 * These are agent tools that use nested streaming - we only want to show
 * the nested agent's tool calls, not the wrapper.
 */
const WRAPPER_AGENT_TOOLS_TO_SKIP = [
  "webFetchAgent",
  // Add more wrapper agent tools here as needed
];

/**
 * Tool name for human-in-the-loop question asking.
 * This tool has no execute function - it's handled client-side.
 */
const ASK_USER_QUESTIONS_TOOL_NAME = "askUserQuestionsTool";

/**
 * Tools that should keep their full output in chat history.
 * All other tools will have their output filtered to only status/message fields
 * to prevent large data (file contents, oldContents, etc.) from bloating chat history.
 */
const TOOLS_TO_KEEP_FULL_OUTPUT = new Set([
  ASK_USER_QUESTIONS_TOOL_NAME, // Needs answers and skipped fields for continuation
]);

/**
 * Pending user input data for tools that require client-side handling.
 */
export interface PendingUserInput {
  toolCallId: string;
  toolName: string;
  questions: {
    id: string;
    question: string;
    type: "text" | "choice" | "multiChoice";
    options?: string[];
    required?: boolean;
    placeholder?: string;
  }[];
  context: string;
}

// Add type definition at the top of the file
type MessageStep = {
  text?: string;
  toolCall?: {
    toolCallId: string;
    name: string;
    argumentsParsed: any;
  };
  toolResult?: string;
  // Built-in provider reasoning (new structured approach)
  reasoning?: {
    content: string;
    thinkingTimeSeconds?: number;
  };
};

interface ChatResponse {
  status: number;
  data: {
    steps: any[];
    reasoning?: string;
    affectedFiles?: string[];
    planOfAction?: string;
    search?: { summary?: string; sources?: any[] };
    searchSummaryDeprecated?: string;
    filesInContext?: string[];
    promptsConsumed?: number;
    toolCallsCount?: number;
    inputTokens?: number;
    outputTokens?: number;
  };
  traceId?: string;
  fraudWarning?: string;
}

const constructMessages = (
  usedIds: { [key: number]: string },
  createdAt: { [key: string]: Date },
  steps: MessageStep[],
) => {
  let idIdx = 0;
  const getId = () => {
    if (!usedIds[idIdx]) {
      usedIds[idIdx] = uuidv4().toString();
      createdAt[usedIds[idIdx]] = new Date();
    }

    idIdx += 1;
    return usedIds[idIdx - 1];
  };

  const newMessages: Message[] = [];

  // Track tool calls and their results to determine status
  const toolCallStatuses: {
    [toolCallId: string]: "in-progress" | "succeeded" | "failed" | "aborted";
  } = {};

  steps.forEach((step) => {
    if (step.toolResult && step.toolCall) {
      try {
        const parsedResult = JSON.parse(step.toolResult);
        // Check for explicit status field or assume success if result exists
        if (parsedResult && typeof parsedResult === "object") {
          if (parsedResult.status) {
            toolCallStatuses[step.toolCall.toolCallId] =
              parsedResult.status === "success" ? "succeeded" : "failed";
          } else {
            // If we have a valid result object but no status, assume succeeded
            toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
          }
        } else {
          // If result is not an object, assume succeeded
          toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
        }
      } catch {
        // If we can't parse the result, assume succeeded
        toolCallStatuses[step.toolCall.toolCallId] = "succeeded";
      }
    }
  });

  // Build messages with proper role separation:
  // - role: "assistant" for text, reasoning, and tool-call
  // - role: "tool" for tool-result
  let currentAssistantContent: any[] = [];

  const flushAssistantMessage = () => {
    if (currentAssistantContent.length > 0) {
      const msgId = getId();
      newMessages.push({
        id: msgId,
        role: "assistant",
        content: JSON.stringify(currentAssistantContent),
        created_at: createdAt[msgId].toISOString(),
      } as Message);
      currentAssistantContent = [];
    }
  };

  const addToolMessage = (
    toolCallId: string,
    toolName: string,
    toolResult: string,
  ) => {
    const msgId = getId();
    // Parse the result and wrap in SDK v6 output format
    let outputValue: any;
    try {
      outputValue = { type: "json", value: JSON.parse(toolResult) };
    } catch {
      outputValue = { type: "text", value: toolResult };
    }
    newMessages.push({
      id: msgId,
      role: "tool",
      content: JSON.stringify([
        {
          type: "tool-result",
          toolCallId,
          toolName,
          output: outputValue,
        },
      ]),
      created_at: createdAt[msgId].toISOString(),
    } as Message);
  };

  steps.forEach((step) => {
    // Add reasoning content to assistant message
    if (step.reasoning) {
      currentAssistantContent.push({
        type: "reasoning",
        reasoning: step.reasoning,
      });
    }

    // Add text content to assistant message
    if (step.text) {
      currentAssistantContent.push({
        type: "text",
        text: step.text,
      });
    }

    // Add tool call to assistant message
    if (step.toolCall) {
      const status =
        toolCallStatuses[step.toolCall.toolCallId] || "in-progress";

      currentAssistantContent.push({
        type: "tool-call",
        args: step.toolCall.argumentsParsed || {},
        toolName: step.toolCall.name,
        toolCallId: step.toolCall.toolCallId,
        status: status,
      });

      // Flush assistant message before adding tool result
      flushAssistantMessage();

      // Add tool result as separate tool message
      if (step.toolResult) {
        addToolMessage(
          step.toolCall.toolCallId,
          step.toolCall.name,
          step.toolResult,
        );
      }
    }
  });

  // Flush any remaining assistant content
  flushAssistantMessage();

  return newMessages;
};

const filterCodeEditStreamFromText = (text: string | null): string => {
  // Remove code-edit-stream blocks from text content
  return (text || "").replace(
    /<code-edit-stream>[\s\S]*?<\/code-edit-stream>/g,
    "",
  );
};

const getTextForAIFromMessageContext = (messageContext: MessageContext) => {
  if (!messageContext) {
    return "";
  }

  let fullText = "";

  if (messageContext?.chatOnlyMode) {
    fullText +=
      "This message was in chat only mode, any changes were not applied.\n\n";
  }

  if (messageContext?.filesWithErrors?.length) {
    fullText += "The following files had errors and were not applied:\n";
    fullText += messageContext?.filesWithErrors?.join("\n");
    fullText += "\n\n";
  }

  if (messageContext?.hadError) {
    fullText +=
      "There was an error in this message, none of the changes were applied.\n\n";
  }

  return fullText.trim();
};

const processMessage = (msg: Message, allEditedFiles?: Set<string>) => {
  if (allEditedFiles && msg.message_context?.editedFiles) {
    msg.message_context.editedFiles.forEach((file) => {
      allEditedFiles.add(file);
    });
  }

  const role = msg.role;

  // Handle tool messages (role: "tool") - these contain tool results
  if (msg.role === "tool") {
    let contentBlocks;
    if (typeof msg.content === "string" && msg.content.startsWith("[")) {
      try {
        contentBlocks = JSON.parse(msg.content);
      } catch {
        contentBlocks = [{ type: "tool-result", result: msg.content }];
      }
    } else if (Array.isArray(msg.content)) {
      contentBlocks = msg.content;
    } else {
      contentBlocks = [{ type: "tool-result", result: msg.content }];
    }

    const filteredBlocks = contentBlocks
      .map((block: any) => {
        // Get the output value, handling legacy formats
        let output = block.output;
        if (!output) {
          // Legacy: result was a string, wrap in SDK v6 format
          const rawResult = block.result || block.content;
          try {
            output = {
              type: "json",
              value:
                typeof rawResult === "string"
                  ? JSON.parse(rawResult)
                  : rawResult,
            };
          } catch {
            output = { type: "text", value: String(rawResult) };
          }
        }

        // Keep full output for tools in the allowlist (e.g., askUserQuestionsTool needs answers field)
        if (TOOLS_TO_KEEP_FULL_OUTPUT.has(block.toolName)) {
          return {
            type: "tool-result",
            toolCallId: block.toolCallId,
            toolName: block.toolName,
            output,
          };
        }

        // For all other tools, filter to only status and message fields. this prevents bloating chat history
        const outputValue = output?.value || output;
        const filteredResult: any = {};

        if (outputValue?.status !== undefined) {
          filteredResult.status = outputValue.status;
        }
        if (outputValue?.message !== undefined) {
          filteredResult.message = outputValue.message;
        }

        // Skip this block entirely if no status or message
        if (Object.keys(filteredResult).length === 0) {
          return null;
        }

        return {
          type: "tool-result",
          toolCallId: block.toolCallId,
          toolName: block.toolName,
          output: { type: "json", value: filteredResult },
        };
      })
      .filter(Boolean); // Remove null entries

    // If all blocks were filtered out, skip this message entirely
    if (filteredBlocks.length === 0) {
      return null;
    }

    return {
      role: "tool",
      content: filteredBlocks,
    };
  }

  // Legacy handling for old "tool-result" role (treat as user message for backwards compat)
  if (msg.role === "tool-result") {
    return {
      role: "user",
      content: msg.content,
    };
  }

  const textForMessageContext = getTextForAIFromMessageContext(
    // @ts-expect-error TS2345
    msg.message_context,
  );
  const messageContextBlocks = textForMessageContext
    ? [
        {
          type: "text",
          text: textForMessageContext,
        },
      ]
    : [];

  let inputBlocks;
  if (Array.isArray(msg.content)) {
    inputBlocks = msg.content;
  } else if (typeof msg.content === "string" && msg.content?.startsWith("[")) {
    try {
      inputBlocks = JSON.parse(msg.content);
    } catch (e) {
      console.error(e);
    }
  }

  if (!inputBlocks) {
    return {
      role,
      content: [
        {
          type: "text",
          text: filterCodeEditStreamFromText(msg.content),
        },
        ...messageContextBlocks,
      ],
    };
  }

  // If it starts with "[" it could be content blocks
  try {
    let invalidBlocks = false;
    const parsedContent = inputBlocks
      ?.map((block) => {
        if (block.type === "text") {
          return {
            type: "text",
            text: filterCodeEditStreamFromText(block.text),
          };
        } else if (block.type === "tool-call") {
          // Keep tool-call as proper structure for SDK v6
          return {
            type: "tool-call",
            toolCallId: block.toolCallId,
            toolName: block.toolName,
            args: block.args || {},
          };
        } else if (block.type === "tool-result") {
          // Tool results should now be in separate messages with role: "tool"
          // But handle legacy inline tool-results for backwards compatibility
          let output = block.output;
          if (!output) {
            // Legacy format - wrap in SDK v6 output format
            const rawResult = block.result || block.content;
            try {
              output = {
                type: "json",
                value:
                  typeof rawResult === "string"
                    ? JSON.parse(rawResult)
                    : rawResult,
              };
            } catch {
              output = { type: "text", value: String(rawResult) };
            }
          }
          return {
            type: "tool-result",
            toolCallId: block.toolCallId,
            toolName: block.toolName,
            output,
          };
        } else {
          invalidBlocks = true;
          return block;
        }
      })
      ?.filter(Boolean); // Remove null entries

    if (invalidBlocks) {
      return {
        role,
        content: [
          {
            type: "text",
            text: JSON.stringify(parsedContent),
          },
          ...messageContextBlocks,
        ],
      };
    }

    return {
      role,
      // Filter out empty blocks
      content: parsedContent
        .filter((block) => block.type !== "text" || block.text)
        .concat(messageContextBlocks),
    };
  } catch (e) {
    return {
      role,
      content: [
        {
          type: "text",
          text: filterCodeEditStreamFromText(msg.content),
        },
        ...messageContextBlocks,
      ],
    };
  }
};

export interface AIChatInput {
  feature_title: string;
  feature_prd: string;
  visual_context: any[];
  user_selection_context: string;
  mermaid_diagram: string;
  selected_elements: string;
  storyboards_in_canvas: string;
  use_text_editor: boolean;
  token_soft_limit: number;
  layout_mode: string;
  left_tab: string;
  blind_edited_files: string[];
  chat_only_mode: boolean;
  use_reasoning: boolean;
  use_search: boolean;
  is_free_prompt: boolean;
  code_generation_model: string;

  // For aiChatChangeCodeSnippet only
  file_contents?: string;
  diff_code_snippet?: string;
  selected_code_snippet?: string;

  // Page context (optional - only provided when pages feature is active)
  page_config_path?: string; // Path to the .page.json file (relative to canvas root)
  page_name?: string;
}

/**
 * Pending tool result for continuation after user input.
 * Used when askUserQuestionsTool needs user answers before continuing.
 */
export interface PendingToolResult {
  toolCallId: string;
  toolName: string;
  result: {
    answers: Array<{
      questionId: string;
      answer: string | string[];
    }>;
    skipped: boolean;
  };
}

interface ChatWithAIParams {
  supabaseJWT: string;
  canvasId: string;
  requestPath: string;
  messagesCanSummarize: Message[];
  messagesDoNotSummarize: Message[];
  userPrompt: string;
  aiChatInput: AIChatInput;
  onStream?: (newMessages: Message[]) => void;
  featureFlags?: { supabase_v2: boolean };
  abortController?: AbortController;
  isFixBrokenResponse?: boolean;
}

export const chatWithAI = async ({
  supabaseJWT,
  requestPath,
  canvasId,
  messagesCanSummarize,
  messagesDoNotSummarize,
  userPrompt,
  onStream,
  aiChatInput,
  abortController,
  isFixBrokenResponse,
  featureFlags = { supabase_v2: true },
}: ChatWithAIParams): Promise<{
  newMessages?: Message[];
  usage?: any;
  error?: string;
  filesInContext?: string[];
  traceId?: string;
  promptsConsumed?: number;
  toolCallsCount?: number;
  inputTokens?: number;
  outputTokens?: number;
  fraudWarning?: string;
  /** Present when AI called askUserQuestionsTool and is waiting for user input */
  pendingUserInput?: PendingUserInput;
}> => {
  const setStreamingTokenCount =
    useAiActionStore.getState().setStreamingTokenCount;
  const resetStreamingTokenCount =
    useAiActionStore.getState().resetStreamingTokenCount;

  // Reset token count at the start of the request
  resetStreamingTokenCount();
  const allEditedFiles = new Set<string>();

  // Step 1: send the conversation and available functions to GPT
  const chatHistoryMessages: any = [...messagesCanSummarize]
    .slice(-MAX_PREVIOUS_MESSAGES)
    .map((msg) => processMessage(msg, allEditedFiles))
    .filter((msg) => {
      if (!msg) return false;
      if (Array.isArray(msg.content)) {
        return msg.content.length > 0;
      }

      return msg.content;
    });

  const chatHistoryDoNotSummarizeMessages: any = [...messagesDoNotSummarize]
    .map((msg) => processMessage(msg))
    .filter((msg) => {
      if (!msg) return false;
      if (Array.isArray(msg.content)) {
        return msg.content.length > 0;
      }
      return msg.content;
    });

  const url = `${
    import.meta.env.VITE_BACKEND_BASE_URL
  }/canvases/${canvasId}/ai/vercel-ai/${requestPath}`;

  const posthog_flags = [];

  if (featureFlags?.supabase_v2) {
    // @ts-expect-error TS2345
    posthog_flags.push("supabase-v2");
  }

  const body = {
    chat_history: chatHistoryMessages,
    chat_history_to_not_summarize: chatHistoryDoNotSummarizeMessages,
    user_prompt: userPrompt,
    posthog_flags,
    all_edited_files: Array.from(allEditedFiles),
    is_fix_broken_response: isFixBrokenResponse,
    ...aiChatInput,
  };

  const stream = await generateJsonStream(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${supabaseJWT}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal: abortController?.signal,
  });

  let res: ChatResponse | null = null;
  const streamingSteps: MessageStep[] = [];
  let currentText = "";
  let currentReasoning = "";
  const usedIds: { [key: number]: string } = {};
  const createdAt: { [key: string]: Date } = {};
  let thinkingTimeSeconds: number | null = null;
  let reasoningStartTime: number | null = null;
  const toolCallRawArgs = new Map<string, string>();

  let lastParseTime = Date.now();

  for await (const chunk of stream) {
    // Allow more expensive operations to be done less often (20FPS)
    const now = Date.now();
    const timeSinceLastParse = now - lastParseTime;
    const passedThrottle = timeSinceLastParse > 1000 / 20;
    if (passedThrottle) {
      lastParseTime = now;
    }

    if (chunk.status === -1 && chunk.message === "intermediate") {
      const parsedChunk = JSON.parse(chunk.data);

      if (parsedChunk.type === "tool-call") {
        // Skip wrapper agent tool calls - only show nested agent's tool calls
        if (
          WRAPPER_AGENT_TOOLS_TO_SKIP.includes(parsedChunk.fullValue.toolName)
        ) {
          continue;
        }

        if (currentText) {
          streamingSteps.push({ text: currentText });
          currentText = "";
        }

        const existingIndex = streamingSteps.findIndex(
          (step) =>
            step.toolCall?.toolCallId === parsedChunk.fullValue.toolCallId,
        );

        if (existingIndex !== -1) {
          streamingSteps[existingIndex].toolCall!.argumentsParsed =
            parsedChunk.fullValue.args;
        } else {
          streamingSteps.push({
            toolCall: {
              toolCallId: parsedChunk.fullValue.toolCallId,
              name: parsedChunk.fullValue.toolName,
              argumentsParsed: parsedChunk.fullValue.args,
            },
          });
        }
      }

      if (parsedChunk.type === "tool-result") {
        // Skip wrapper agent tool results
        if (WRAPPER_AGENT_TOOLS_TO_SKIP.includes(parsedChunk.toolName)) {
          continue;
        }

        // Check if this is an applyFileEditsTool result with an activityId
        // Trigger activity stream refetch immediately when AI makes file changes
        if (
          parsedChunk.toolName === "applyFileEditsTool" &&
          parsedChunk.result?.activityId
        ) {
          useActivityStore.getState().triggerRefetchActivityStream();
        }

        const toolCallIndex = streamingSteps.findIndex(
          (step) => step.toolCall?.toolCallId === parsedChunk.toolCallId,
        );

        if (toolCallIndex !== -1) {
          streamingSteps[toolCallIndex].toolResult = JSON.stringify(
            parsedChunk.result,
          );
        } else {
          console.warn(
            `Received tool-result for unknown tool call: ${parsedChunk.toolCallId}`,
          );
        }
      }

      if (parsedChunk.type === "tool-call-delta") {
        // Skip wrapper agent tool deltas
        if (WRAPPER_AGENT_TOOLS_TO_SKIP.includes(parsedChunk.chunk.toolName)) {
          continue;
        }

        const toolCallId = parsedChunk.chunk.toolCallId;

        // Find or create the tool call in streamingSteps
        let toolCallIndex = streamingSteps.findIndex(
          (step) => step.toolCall?.toolCallId === toolCallId,
        );

        if (toolCallIndex === -1) {
          // First delta - flush any accumulated text before creating tool call
          if (currentText) {
            streamingSteps.push({ text: currentText });
            currentText = "";
          }

          // Create new tool call entry
          streamingSteps.push({
            toolCall: {
              toolCallId: toolCallId,
              name: parsedChunk.chunk.toolName,
              argumentsParsed: null,
            },
          });
          toolCallIndex = streamingSteps.length - 1;
          toolCallRawArgs.set(toolCallId, "");
        }

        // Accumulate arguments
        const currentArgs = toolCallRawArgs.get(toolCallId) || "";
        const updatedArgs = currentArgs + parsedChunk.chunk.argsTextDelta;
        toolCallRawArgs.set(toolCallId, updatedArgs);

        // Parse and update only on throttle (to reduce CPU usage)
        if (passedThrottle) {
          const toolCall = streamingSteps[toolCallIndex].toolCall!;
          toolCall.argumentsParsed = fixJson(updatedArgs).fixedObj;
        }
      }

      // Text streaming
      if (parsedChunk.type === "text-delta") {
        currentText += parsedChunk.chunk;

        // Extract thinking time from reasoning-timing blocks
        if (
          parsedChunk.chunk.includes("<reasoning-timing>") &&
          parsedChunk.chunk.includes("</reasoning-timing>")
        ) {
          try {
            const timingMatch = parsedChunk.chunk.match(
              /<reasoning-timing>\s*(.+?)\s*<\/reasoning-timing>/s,
            );
            if (timingMatch) {
              const timingJson = JSON.parse(timingMatch[1]);
              thinkingTimeSeconds = timingJson.thinking_time_seconds;
            }
          } catch (error) {
            console.error("Failed to extract thinking time:", error);
          }
        }
      }

      // Built-in provider reasoning events
      if (parsedChunk.type === "reasoning-start") {
        currentReasoning = "";
        reasoningStartTime = Date.now();
      }

      if (parsedChunk.type === "reasoning-delta") {
        currentReasoning += parsedChunk.chunk;
      }

      if (parsedChunk.type === "reasoning-end") {
        // Calculate thinking time
        if (reasoningStartTime !== null) {
          thinkingTimeSeconds = Math.round(
            (Date.now() - reasoningStartTime) / 1000,
          );
        }
      }

      // Create messages from current state
      const currentSteps = [...streamingSteps];
      if (currentText) {
        currentSteps.push({ text: currentText });
      }

      if (passedThrottle) {
        // Prepend reasoning if it's being streamed (NEW: structured format)
        let stepsWithReasoning = currentSteps;

        if (currentReasoning) {
          stepsWithReasoning = [
            {
              reasoning: {
                content: currentReasoning,
                thinkingTimeSeconds: thinkingTimeSeconds ?? undefined,
              },
            },
            ...currentSteps,
          ];
        }

        const newMessages = constructMessages(
          usedIds,
          createdAt,
          stepsWithReasoning,
        );
        if (newMessages?.length) {
          const latestMessage = newMessages[newMessages.length - 1];
          if (latestMessage) {
            const segments = parseMessageContentBlocks(latestMessage);
            const tokenCount = countEligibleTokensFromSegments(segments);
            setStreamingTokenCount(tokenCount);
          }

          onStream?.(newMessages);
        }
      }
    } else {
      res = chunk as ChatResponse;
      break;
    }
  }

  // @ts-expect-error TS18047
  if (res.status !== 200) {
    console.error({
      // @ts-expect-error TS18047
      msg: res.message,
      // @ts-expect-error TS18047
      internalErrorStr: res.internalErrorStr,
    });
    // @ts-expect-error TS18047
    return { error: res.message, traceId: res.traceId };
  }
  // TODO: Get rid of searchSummaryDeprecated on or after 14th March 2025. It exists for backwards compatibility.
  const {
    steps,
    reasoning,
    affectedFiles,
    planOfAction,
    search,
    searchSummaryDeprecated,
    promptsConsumed,
    toolCallsCount,
    inputTokens,
    outputTokens,
    // @ts-expect-error TS18047
  } = res.data;

  const traceId = res?.traceId;
  const fraudWarning = res?.fraudWarning;

  // @ts-expect-error TS18047
  const filesInContext = res.data.filesInContext;

  const reasoningObject = {
    reasoning: reasoning || "",
    plan_of_action: planOfAction || "",
    affected_files: affectedFiles || [],
    summary_of_search_results: search?.summary || searchSummaryDeprecated || "",
  };

  let reasoningText = "";
  if (reasoning) {
    reasoningText = `<reasoning>\n${JSON.stringify(
      reasoningObject,
    )}\n</reasoning>\n`;

    // Add reasoning-timing block if we have thinking time
    if (thinkingTimeSeconds !== null) {
      reasoningText += `<reasoning-timing>\n${JSON.stringify({
        thinking_time_seconds: thinkingTimeSeconds,
      })}\n</reasoning-timing>\n`;
    }
  }

  let searchGroundingSourcesText = "";
  if (search?.sources?.length) {
    searchGroundingSourcesText = `\n<search-grounding-sources>\n${JSON.stringify(
      {
        searchGroundingSources: search.sources,
      },
    )}\n</search-grounding-sources>\n`;
  }

  // Add any remaining currentText to streamingSteps before finalizing
  if (currentText) {
    streamingSteps.push({ text: currentText });
    currentText = "";
  }

  const finalSteps: MessageStep[] = [
    ...(currentReasoning
      ? [
          {
            reasoning: {
              content: currentReasoning,
              thinkingTimeSeconds: thinkingTimeSeconds ?? undefined,
            },
          },
        ]
      : []),
    // OLD: Custom reasoning (XML format - keep for backward compatibility)
    ...(reasoningText ? [{ text: reasoningText }] : []),
    ...streamingSteps,
    ...(searchGroundingSourcesText
      ? [{ text: searchGroundingSourcesText }]
      : []),
  ];

  const finalMessages = constructMessages(usedIds, createdAt, finalSteps);

  // Flush the final messages to the stream callback to ensure UI is up to date
  onStream?.(finalMessages);

  // Check for pending askUserQuestionsTool (tool call without result)
  // This indicates the AI is waiting for user input
  let pendingUserInput: PendingUserInput | undefined;
  for (const step of finalSteps) {
    if (
      step.toolCall?.name === ASK_USER_QUESTIONS_TOOL_NAME &&
      !step.toolResult
    ) {
      const args = step.toolCall.argumentsParsed;
      pendingUserInput = {
        toolCallId: step.toolCall.toolCallId,
        toolName: step.toolCall.name,
        questions: args?.questions || [],
        context: args?.context || "",
      };
      break;
    }
  }

  return {
    newMessages: finalMessages,
    filesInContext: filesInContext,
    traceId,
    promptsConsumed,
    toolCallsCount,
    inputTokens,
    outputTokens,
    fraudWarning,
    pendingUserInput,
  };
};

export const useChatWithAI = () => {
  const canvasId = useCanvasId();
  const { session } = useSession();
  const baseChatInput = useBaseInputsForPrompt();

  // Get selected issue IDs for PM Agent
  const selectedIssueIds = useIssueSelectionStore(
    (state) => state.selectedIssueIds,
  );

  // Context the user added in the PRD tab
  const featurePrd = useIssuesStore(
    (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
  );

  const { contextWithImages: prdImageContext } = getContextFromFeaturePrd(
    featurePrd ?? undefined,
  );

  const { createAssistantMessage } = useCreateMessageForChat();

  return async ({
    isRetry,
    abortController,
    onStreamedMessages,
    ...messageOpts
  }: ChatMessageOptions & {
    isRetry?: boolean;
    abortController?: AbortController;
    onStreamedMessages: (messages: Message[]) => void;
  }) => {
    const supabaseJWT = (await session?.getToken()) || "";
    const visualContext = [
      ...prdImageContext,
      ...(messageOpts.attachedFiles || []),
    ];

    // PM Agent uses a different request path
    const requestPath =
      messageOpts.agentChoice === AI_AGENT.PM_AGENT
        ? "aiChatPMAgent"
        : "aiChatMaxAgent";

    const aiChatInput = {
      ...baseChatInput,
      additional_instructions: messageOpts.additionalInstructions,
      use_text_editor: !messageOpts.disableTextEditor,
      blind_edited_files: messageOpts.criticalFiles ?? [], // Its called blind_edited_files but in the backend its treated as "critical files to always include"
      chat_only_mode: messageOpts.chatOnlyMode,
      use_reasoning: messageOpts.useReasoning,
      use_search: messageOpts.useSearch,
      is_free_prompt: messageOpts.isFreePrompt,
      code_generation_model: messageOpts.codeGenerationModel,
      visual_context: visualContext,
      // Include selected issue IDs for PM Agent
      ...(messageOpts.agentChoice === AI_AGENT.PM_AGENT && {
        selected_issue_ids: Array.from(selectedIssueIds),
      }),
    };
    try {
      const {
        newMessages,
        error,
        filesInContext,
        traceId,
        promptsConsumed,
        toolCallsCount,
        inputTokens,
        outputTokens,
        fraudWarning,
        pendingUserInput,
      } = await chatWithAI({
        requestPath,
        canvasId,
        messagesCanSummarize: messageOpts.messagesCanSummarize,
        messagesDoNotSummarize: messageOpts.messagesDoNotSummarize,
        supabaseJWT,
        userPrompt: messageOpts.userPrompt,
        onStream: onStreamedMessages,
        abortController,
        aiChatInput,
        isFixBrokenResponse: isRetry,
      });

      // Show fraud warning if user was flagged for system abuse
      if (fraudWarning) {
        toast.warning(fraudWarning);
      }

      if (error) {
        toast.error(`AI request failed: ${error}`);
        track("AI_CHAT_ERROR", {
          error: `AI request failed: ${error}`,
          data: {
            requestPath,
            isRetry,
            traceId,
          },
        });
      }

      return {
        newMessages,
        error,
        filesInContext,
        requestPath,
        traceId,
        promptsConsumed,
        toolCallsCount,
        inputTokens,
        outputTokens,
        fraudWarning,
        pendingUserInput,
      };
    } catch (e) {
      logAxiosError("Error sending message to AI", e);
      const status = (e as any)?.response?.status;
      const isServerError = typeof status === "number" && status >= 500;

      const messages: Message[] = [];
      if (e.name.includes("AbortError")) {
        track("AI_CHAT_WARNING", {
          warning: `Chat response aborted`,
        });
      } else {
        const data = {
          requestPath,
          isRetry,
          message: getAxiosErorrMessage(e),
          ...(typeof status === "number" && { status_code: status }),
        };

        if (isServerError) {
          track("AI_CHAT_ERROR", {
            error: `Server Error calling chatWithAI endpoint (Status ${status})`,
            data,
          });
        } else {
          track("AI_CHAT_WARNING", {
            warning: `Non-500 error calling chatWithAI endpoint`,
            data,
          });
        }
        messages.push(
          createAssistantMessage({
            content: `There's been an error processing your request. Please try again.`,
            message_context: {
              hadError: true,
            },
          }),
        );
      }

      return {
        newMessages: messages,
        error: `Error thrown while trying to chat with AI: ${getAxiosErorrMessage(
          e,
        )}`,
        filesInContext: [],
        requestPath,
        traceId: undefined,
        promptsConsumed: undefined,
        toolCallsCount: undefined,
        inputTokens: undefined,
        outputTokens: undefined,
        fraudWarning: undefined,
        pendingUserInput: undefined,
      };
    }
  };
};
